import { useEffect, useRef, useState } from 'react';

// U-ADM-01 · pre-launch audit hardening.
//
// Donor phone / email / address and volunteer national-ID / phone / email
// used to render in plaintext in the admin tables. Anyone glancing at
// a shared-office monitor could copy the data, and a stray screenshot
// or Loom recording would leak it in bulk.
//
// This component masks the value by default (keeping enough of a hint
// for staff to recognise the row: first + last characters), and reveals
// the full value on an explicit click. The reveal auto-expires after
// REVEAL_MS milliseconds so a distracted admin doesn't leave PII on
// screen. `kind='tel' | 'email'` also swap the reveal-time action link
// (tel: / mailto:) — the underlying href is only populated while
// revealed so a Copy-Link-Location on the masked state leaks nothing.
//
// This is a client-side UX guard, not a hard security boundary — the
// data is in the row that fed the component. The real gate is server-
// side RLS (donations + volunteers policies). Treat this as the same
// class as "don't paste passwords into chat" hygiene: it makes the
// office-safe case work without changing what a determined attacker
// with an admin JWT can access.

type Kind = 'text' | 'tel' | 'email' | 'id';

interface Props {
  value: string | null | undefined;
  kind?: Kind;
  // When true, always render the full value (e.g. super-admin drilldown).
  // Do not thread this through casually — it defeats the point of the
  // component.
  reveal?: boolean;
  className?: string;
  style?: React.CSSProperties;
  // Fallback text when value is null/empty (default: '—').
  placeholder?: string;
}

const REVEAL_MS = 10_000;

function mask(value: string, kind: Kind): string {
  if (!value) return value;

  if (kind === 'email') {
    const [local, domain] = value.split('@');
    if (!domain) return maskGeneric(value);
    const localMasked =
      local.length <= 2 ? '••' : `${local[0]}${'•'.repeat(Math.min(6, local.length - 2))}${local[local.length - 1]}`;
    return `${localMasked}@${domain}`;
  }

  if (kind === 'tel') {
    // Show last 3 digits so an admin can still recognise the row.
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 4) return '••••';
    const tail = digits.slice(-3);
    return `${'•'.repeat(digits.length - 3)}${tail}`;
  }

  if (kind === 'id') {
    // Egyptian national ID: 14 digits. Show only first + last.
    if (value.length <= 4) return '••••';
    return `${value[0]}${'•'.repeat(value.length - 2)}${value[value.length - 1]}`;
  }

  return maskGeneric(value);
}

function maskGeneric(value: string): string {
  if (value.length <= 2) return '••';
  return `${value[0]}${'•'.repeat(Math.max(1, value.length - 2))}${value[value.length - 1]}`;
}

export default function MaskedPii({
  value,
  kind = 'text',
  reveal: forceReveal = false,
  className,
  style,
  placeholder = '—',
}: Props) {
  const [revealed, setRevealed] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  if (!value) return <span className={className} style={style}>{placeholder}</span>;

  const showFull = forceReveal || revealed;
  const shown = showFull ? value : mask(value, kind);

  const doReveal = () => {
    if (forceReveal) return;
    if (timer.current !== null) window.clearTimeout(timer.current);
    setRevealed(true);
    timer.current = window.setTimeout(() => setRevealed(false), REVEAL_MS);
  };

  const commonProps = {
    className,
    style: {
      cursor: forceReveal ? 'default' : 'pointer',
      userSelect: showFull ? ('text' as const) : ('none' as const),
      ...style,
    },
    title: showFull ? '' : 'اضغط للعرض لمدة ١٠ ثواني',
    onClick: doReveal,
  };

  // For tel + email, the anchor is only rendered when revealed —
  // otherwise the browser context menu could leak the value via
  // Copy-Link-Location.
  if (kind === 'tel' && showFull) {
    return <a href={`tel:${value}`} {...commonProps}>{shown}</a>;
  }
  if (kind === 'email' && showFull) {
    return <a href={`mailto:${value}`} {...commonProps}>{shown}</a>;
  }

  return <span {...commonProps}>{shown}</span>;
}
